import React, { useRef, useEffect, useState } from "react";
import { Box, Tooltip, IconButton, Typography } from "@mui/material";
import { Icon } from "@fluentui/react/lib/Icon";
import { IniInfo } from "system/types/interfaces";
import { DeleteRegular, EditRegular } from "@fluentui/react-icons";

interface SiteSubnetRowProps {
  item: IniInfo;
  index: number;
  onAdd: (index: number) => void;
  onDelete: (name: string) => void;
  onUndoDelete: (name: string) => void;
  onEdit: (item: IniInfo) => void;
  readOnlyViewer: boolean;
  searchTerm: string;
  style?: React.CSSProperties;
}

export const SiteSubnetRow: React.FC<SiteSubnetRowProps> = ({
  item,
  index,
  onAdd,
  onDelete,
  onUndoDelete,
  onEdit,
  readOnlyViewer,
  searchTerm,
  style,
}) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);
  const [isNameOverflowing, setIsNameOverflowing] = useState(false);
  const [isValueOverflowing, setIsValueOverflowing] = useState(false);

  const isDeleted = item.isDeleted || false;

  const isCommentRow = item.isComment === true;

  useEffect(() => {
    if (nameRef.current) {
      setIsNameOverflowing(nameRef.current.scrollWidth > nameRef.current.clientWidth);
    }
    if (valueRef.current) {
      setIsValueOverflowing(valueRef.current.scrollWidth > valueRef.current.clientWidth);
    }
  }, [item.name, item.value]);

  const highlightText = (text: string) => {
    if (!searchTerm) return <span>{text}</span>;
    const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escapedSearch})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={i} style={{ backgroundColor: "yellow", color: "black" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const rowStyle: React.CSSProperties = {
    ...style,
    display: "grid",
    gridTemplateColumns: "400px auto 270px",
    alignItems: "center",
    borderBottom: "0.5px solid #515151",
    padding: "8px 16px",
    backgroundColor: isDeleted ? "#2a2a2a" : "inherit",
    height: "50px",
    overflow: "hidden",
  };

  const scrollCellStyles = {
    "&::-webkit-scrollbar": {
      height: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#555",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      height: "8px",
      backgroundColor: "#ccc",
    },
  };

  const tooltipStyles = {
    backgroundColor: "#333",
    color: "#fff",
    fontSize: "12px",
    padding: "8px",
    borderRadius: "4px",
    maxWidth: "500px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  };

  return (
    <Box sx={{ "&:hover": { backgroundColor: isDeleted ? "#2a2a2a" : "#333" } }} style={rowStyle}>

      {/* Column 1: Site Name */}
      <Box
        ref={nameRef}
        sx={{
          overflowX: "auto",
          whiteSpace: "nowrap",
          color: isCommentRow
            ? "#4caf50"
            : isDeleted
            ? "#d5878b"
            : "#fff",
          fontStyle: isCommentRow ? "italic" : "normal",
          textDecoration: isDeleted ? "line-through" : "none",
        }}
      >
        <Tooltip
          title={isCommentRow ? item.value : item.name}
          disableHoverListener={!isNameOverflowing}
        >
          <Typography
            variant="body1"
            sx={{ color: "inherit", fontStyle: "inherit" }}
          >
            {highlightText(isCommentRow ? item.value || "" : item.name)}
          </Typography>
        </Tooltip>
      </Box>

      {/* Column 2: IP Ranges */}
      <Box
        ref={valueRef}
        component="div"
        sx={{
          overflowX: "auto",
          whiteSpace: "nowrap",
          position: "relative",
          color: isDeleted ? "#d5878b" : "#fff",
          textDecoration: isDeleted ? "line-through" : "none",
          ...scrollCellStyles,
        }}
      >
        {!isCommentRow && (
          <Tooltip
            title={item.value || ""}
            disableHoverListener={!isValueOverflowing}
            sx={tooltipStyles}
          >
            <Typography
              variant="body1"
              sx={{
                fontStyle: "italic",
                textDecoration: isDeleted ? "line-through" : "none",
                whiteSpace: "nowrap",
              }}
            >
              {highlightText(item.value || "")}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Column 3: Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px" }}>
        {item.pendingChanges && (
          <Typography
            variant="body2"
            sx={{
              backgroundColor: isDeleted ? "#d5878b" : "#fbc02d",
              color: "#000",
              padding: "3px 6px",
              borderRadius: "4px",
              fontWeight: "bold",
            }}
          >
            {isDeleted ? "Deleted Row" : "Pending Changes"}
          </Typography>
        )}
        <Tooltip title="Add line">
          <IconButton size="small" color="primary" onClick={() => onAdd(index)} hidden={readOnlyViewer}>
            <Icon iconName="Add" />
          </IconButton>
        </Tooltip>
        {isDeleted ? (
          <Tooltip title="Undo Delete" arrow>
            <IconButton size="small" onClick={() => onUndoDelete(item.name)}>
              <Icon iconName="Undo" />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Delete" arrow>
              <IconButton size="small" color="error" sx={{ color: "#d54c52" }} onClick={() => onDelete(item.name)} hidden={readOnlyViewer}>
                <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Site" arrow>
              <IconButton size="small" sx={{ color: "#8a8886" }} onClick={() => onEdit(item)} hidden={readOnlyViewer}>
                <EditRegular fontSize={22} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};
